import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import RequireAuth from './components/RequireAuth';
import Layout from './components/Layout';
import LayoutFormateur from './components/LayoutFormateur';
import MessagerieFormateurs from './pages/MessagerieFormateurs';
import EspacePedagogique from './pages/EspacePedagogique';
import { useAuth } from './context/AuthContext';
import LayoutPublic from './components/LayoutPublic';
import { Toasts } from './components/Toasts';
import Connexion from './pages/Connexion';
import DemandesEtDevis, { RedirectionVersDevis } from './pages/DemandesEtDevis';
import Calendrier from './pages/Calendrier';
import SeanceDetail from './pages/SeanceDetail';
import Contrats from './pages/Contrats';
import DocumentsSession from './pages/DocumentsSession';
import Rappels from './pages/Rappels';
import ParametrageDevis from './pages/ParametrageDevis';
import ParametrageFormations from './pages/ParametrageFormations';
import ParametrageRappels from './pages/ParametrageRappels';
import ParametrageAcces from './pages/ParametrageAcces';
import Signature from './pages/Signature';
import Clients from './pages/Clients';
import ClientDossier from './pages/ClientDossier';
import ParametrageDocuments from './pages/ParametrageDocuments';
import ParametrageModelesContrat from './pages/ParametrageModelesContrat';
import ParametrageTrello from './pages/ParametrageTrello';
import Formateurs from './pages/Formateurs';
import FormateurFiche from './pages/FormateurFiche';
import FormateursContrats from './pages/FormateursContrats';
import Historique from './pages/Historique';
import Corbeille from './pages/Corbeille';
import SignatureContrat from './pages/SignatureContrat';
import Emargement from './pages/Emargement';
import SignatureAttestation from './pages/SignatureAttestation';
import EvaluationPublique from './pages/EvaluationPublique';
import Presence from './pages/Presence';
import Reporting from './pages/Reporting';
import SignaturesRecues from './pages/SignaturesRecues';
import Qcm from './pages/Qcm';
import './assets/styles/TexteBorne.css';
import './assets/styles/Mobile.css';

/**
 * Le châssis interne : l'équipe a la barre latérale et tout le back-office ;
 * un compte formateur n'a que sa fiche, sans barre latérale (le serveur
 * refuse de toute façon le reste).
 */
function ChassisInterne() {
  const { utilisateur } = useAuth();
  return utilisateur?.role === 'formateur' ? <LayoutFormateur /> : <Layout />;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/connexion" element={<Connexion />} />

          {/* Tout le châssis interne est derrière la connexion : sans compte,
              RequireAuth renvoie à /connexion et y revient ensuite. */}
          <Route
            element={
              <RequireAuth>
                <ChassisInterne />
              </RequireAuth>
            }
          >
            <Route index element={<Navigate to="/demande-de-devis" replace />} />
            <Route path="/demande-de-devis" element={<DemandesEtDevis />} />
            <Route path="/demande-de-devis/trello" element={<ParametrageTrello />} />
            {/* Ancienne entrée « Devis » : fondue dans Demande de devis. */}
            <Route path="/devis" element={<RedirectionVersDevis />} />
            <Route path="/contrats" element={<Contrats />} />
            <Route
              path="/convocations"
              element={
                <DocumentsSession
                  type="convocation"
                  etape="convocation"
                  titre="Convocation"
                  videTitre="Aucune convocation ne correspond à cette vue."
                  videSousTitre="Les convocations se génèrent depuis la planification d'un contrat."
                />
              }
            />
            <Route
              path="/feuilles-de-presence"
              element={
                <DocumentsSession
                  type="feuille_presence"
                  etape="feuille"
                  titre="Feuille de présence"
                  videTitre="Aucune feuille de présence ne correspond à cette vue."
                  videSousTitre="Les feuilles de présence se génèrent depuis la planification d'un contrat."
                />
              }
            />
            <Route
              path="/attestations"
              element={
                <DocumentsSession
                  type="attestation"
                  etape="feuille"
                  titre="Attestation"
                  videTitre="Aucune attestation ne correspond à cette vue."
                  videSousTitre="Les attestations se génèrent avec la feuille de présence de chaque journée."
                />
              }
            />
            <Route path="/rappels" element={<Rappels />} />
            <Route path="/calendrier" element={<Calendrier />} />
            <Route path="/calendrier/session/:source/:id" element={<SeanceDetail />} />
            <Route path="/reporting" element={<Reporting />} />
            <Route path="/historique" element={<Historique />} />
            <Route path="/corbeille" element={<Corbeille />} />
            <Route path="/clients" element={<Clients />} />
            <Route path="/clients/:id" element={<ClientDossier />} />
            <Route path="/formateurs" element={<Formateurs />} />
            <Route path="/formateurs/contrats" element={<FormateursContrats />} />
            {/* Les documents des formateurs : mêmes écrans que Convocation et
                Feuille de présence, restreints aux journées qui ont une fiche
                assignée. Déclarées avant « :id » par lisibilité — le routeur
                préfère de toute façon un segment fixe à un paramètre. */}
            <Route
              path="/formateurs/convocations"
              element={
                <DocumentsSession
                  type="convocation_formateur"
                  etape="convocation"
                  titre="Convocations des formateurs"
                  videTitre="Aucune convocation de formateur ne correspond à cette vue."
                  videSousTitre="Elle se produit quand une journée planifiée a un formateur assigné."
                />
              }
            />
            {/* Les mêmes séances que la page Rappel, restreintes à celles
                qui ont un formateur assigné : ce sont les seules où il reçoit
                quelque chose. C'est là qu'on règle ce qu'il lit. */}
            <Route
              path="/formateurs/rappels"
              element={<Rappels avecFormateur titre="Rappels des formateurs" />}
            />
            <Route
              path="/formateurs/feuilles-de-presence"
              element={
                <DocumentsSession
                  type="feuille_presence"
                  etape="feuille"
                  avecFormateur
                  titre="Feuilles de présence des formateurs"
                  videTitre="Aucune feuille de présence ne correspond à cette vue."
                  videSousTitre="Seules les journées qui ont un formateur assigné figurent ici."
                />
              }
            />
            <Route path="/formateurs/messagerie" element={<MessagerieFormateurs />} />
            <Route path="/formateurs/espace-pedagogique" element={<EspacePedagogique />} />
            <Route path="/formateurs/:id" element={<FormateurFiche />} />
            {/* QCM de recrutement : banques clients et formateurs, onglets
                dans la page. */}
            <Route path="/qcm" element={<Qcm />} />
            <Route path="/signature-electronique" element={<Signature />} />
            <Route path="/signature-contrat" element={<SignatureContrat />} />
            {/* Les signatures posées depuis les liens publics d'émargement. */}
            <Route path="/signatures-recues" element={<SignaturesRecues />} />
            <Route path="/parametrage" element={<Navigate to="/parametrage/formations" replace />} />
            <Route path="/parametrage/devis" element={<ParametrageDevis />} />
            <Route path="/parametrage/formations" element={<ParametrageFormations />} />
            <Route path="/parametrage/rappels" element={<ParametrageRappels />} />
            <Route path="/parametrage/documents" element={<ParametrageDocuments />} />
            <Route path="/parametrage/modeles-contrat" element={<ParametrageModelesContrat />} />
            {/* Ancienne adresse : Trello a rejoint Demande de devis. */}
            <Route path="/parametrage/trello" element={<Navigate to="/demande-de-devis/trello" replace />} />
            <Route path="/parametrage/acces" element={<ParametrageAcces />} />
          </Route>

          {/* Pages atteintes par le client depuis le lien reçu par email : hors
              du châssis interne et sans connexion — le jeton fait foi. Les mêmes
              pages sans jeton restent dans le Layout, pour l'entrée « Clients »
              de la barre latérale. */}
          <Route element={<LayoutPublic />}>
            <Route path="/signature-electronique/:token" element={<Signature />} />
            <Route path="/signature-contrat/:token" element={<SignatureContrat />} />
            {/* Émargement en ligne : un lien par personne, reçu le matin de
                la formation (migration 038). */}
            <Route path="/emargement/:token" element={<Emargement />} />
            <Route path="/signature-attestation/:token" element={<SignatureAttestation />} />
            <Route path="/evaluation/:token" element={<EvaluationPublique />} />
            {/* Réponse à une convocation : un lien par destinataire (migration 079). */}
            <Route path="/presence/:token" element={<Presence />} />
          </Route>
        </Routes>
        {/* Notifications flottantes de toutes les pages (envois, actions). */}
        <Toasts />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
